import { useCallback, useEffect, useRef, useState } from 'react'

import {
  LABEL_DRUH_POZEMKU,
  LABEL_ZPUSOB_VYUZITI_POZEMKU,
} from './propertyListAreaTableHelpers'

const AREA_LAYER_URL =
  'https://ags.cuzk.gov.cz/arcgis/rest/services/RUIAN/MapServer/5/query'
const CADASTRE_AREA_CODE_LAYER_URL =
  'https://ags.cuzk.gov.cz/arcgis/rest/services/RUIAN/MapServer/7/query'

const OUT_FIELDS =
  'objectid,id,nespravny,kmenovecislo,poddelenicisla,vymeraparcely,zpusobyvyuzitipozemku,druhcislovanikod,druhpozemkukod,katastralniuzemi,platiod,idtransakce,rizeniid,st_area(shape)'

interface ArcGisFeatureResponse<T = Record<string, unknown>> {
  features: { attributes: T }[]
}

export interface AreaAttributes {
  objectid: number
  id: number
  nespravny: string | null
  kmenovecislo: number | null
  poddelenicisla: number | null
  vymeraparcely: number | null
  zpusobyvyuzitipozemku: number | null
  druhcislovanikod: number | null
  druhpozemkukod: number | null
  katastralniuzemi: number | null
  platiod: number | null
  idtransakce: number | null
  rizeniid: number | null
  'st_area(shape)': number | null
  parcelniCislo?: string | null // derived: kmenovecislo/poddelenicisla
  [k: string]: unknown
}

export interface PropertyListAreaDetailRow extends AreaAttributes {
  key: string
  druhPozemkuText: string | null
  zpusobVyuzitiPozemkuText: string | null
  katastralniUzemiText: string | null
}

const mapLabel = (value: number | null, labels: Record<number, string>) =>
  value != null ? labels[value] ?? null : null

const buildParcelNumber = (
  kmenovecislo: number | null,
  poddelenicisla: number | null,
  druhcislovanikod: number | null
): string | null => {
  if (!kmenovecislo) return null
  const base =
    druhcislovanikod === 1 ? `st. ${kmenovecislo}` : `${kmenovecislo}`
  if (poddelenicisla) return `${base}/${poddelenicisla}`
  return base
}

export const useFetchPropertyListAreaDetail = (whereClause?: string) => {
  const [data, setData] = useState<PropertyListAreaDetailRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const requestIdRef = useRef(0)

  const buildSearchParams = useCallback(() => {
    if (!whereClause) return null
    let parsedWhere: unknown
    try {
      parsedWhere = JSON.parse(whereClause)
    } catch {
      return null
    }
    const parsed = parsedWhere as { where?: string; geometry?: string }
    const params = new URLSearchParams({
      f: 'json',
      where: parsed?.where || '1=0',
      outFields: OUT_FIELDS,
      returnGeometry: 'false',
    })
    if (parsed?.geometry) {
      params.set('geometryType', 'esriGeometryPolygon')
      params.set('spatialRel', 'esriSpatialRelIntersects')
      params.set('geometry', parsed.geometry)
      params.set('inSR', '4326')
      params.set('outSR', '4326')
    }
    return params
  }, [whereClause])

  const fetchData = useCallback(async () => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(null)

    const params = buildSearchParams()
    if (!whereClause || !params) {
      if (requestId === requestIdRef.current) {
        setData([])
        setLoading(false)
      }
      return
    }

    try {
      const response = await fetch(AREA_LAYER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
        signal: controller.signal,
      })

      if (!response.ok) throw new Error('HTTP ' + response.status)

      const featureResponse: ArcGisFeatureResponse<AreaAttributes> =
        await response.json()

      const cadastralCodesSet = new Set<number>()

      for (const feature of featureResponse.features || []) {
        const cadastralCode = feature.attributes.katastralniuzemi as
          | number
          | null
        if (typeof cadastralCode === 'number') {
          cadastralCodesSet.add(cadastralCode)
        }
      }

      const cadastreAreaCodes = Array.from(cadastralCodesSet)
      let cadastreArea: Record<number, { name: string }> | undefined

      if (cadastreAreaCodes.length) {
        const nameParams = new URLSearchParams({
          f: 'json',
          where: `kod IN (${cadastreAreaCodes.join(',')})`,
          outFields: 'objectid,kod,nazev',
          returnGeometry: 'false',
        })

        const cadastreAreaResponse = await fetch(CADASTRE_AREA_CODE_LAYER_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: nameParams.toString(),
          signal: controller.signal,
        })

        if (cadastreAreaResponse.ok) {
          const cadastreAreaResponseJson: ArcGisFeatureResponse<{
            kod: number
            nazev: string
          }> = await cadastreAreaResponse.json()
          cadastreArea = (cadastreAreaResponseJson.features || []).reduce(
            (acc, item) => {
              acc[item.attributes.kod] = { name: item.attributes.nazev }
              return acc
            },
            {} as Record<number, { name: string }>
          )
        }
      }

      const rows: PropertyListAreaDetailRow[] = (
        featureResponse.features || []
      ).map((feature, idx) => {
        const attrs = feature.attributes
        return {
          key: String(attrs.objectid || idx + 1),
          ...attrs,
          parcelniCislo: buildParcelNumber(
            attrs.kmenovecislo,
            attrs.poddelenicisla,
            attrs.druhcislovanikod
          ),
          druhPozemkuText: mapLabel(attrs.druhpozemkukod, LABEL_DRUH_POZEMKU),
          zpusobVyuzitiPozemkuText: mapLabel(
            attrs.zpusobyvyuzitipozemku,
            LABEL_ZPUSOB_VYUZITI_POZEMKU
          ),
          katastralniUzemiText:
            attrs.katastralniuzemi != null
              ? cadastreArea?.[attrs.katastralniuzemi]?.name ||
                String(attrs.katastralniuzemi)
              : null,
        }
      })

      if (requestId === requestIdRef.current) {
        setData(rows)
      }
    } catch (e: unknown) {
      const errorObj = e as { name?: string; message?: string }
      if (errorObj?.name !== 'AbortError') {
        if (requestId === requestIdRef.current) {
          setError(errorObj.message || 'Chyba při načítání')
        }
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [buildSearchParams, whereClause])

  useEffect(() => {
    fetchData()
    return () => abortRef.current?.abort()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}
